<template>
  <div ref="chartContainer" class="flag-threshold-chart">
    <!-- Custom chart title with Download Button -->
    <div class="text-center mb-2 relative">
      <h4 class="text-sm font-bold text-gray-900">{{ metricLabel }} - Flag Threshold: {{ formattedThreshold }}</h4>
      <!-- Download Button on far right -->
      <div class="absolute right-0 top-0">
        <ChartDownloadButton
          :chart-instance="chart"
          chart-name="flag-threshold-chart"
          :is-chart-ready="isChartReady"
          :container-element="chartContainer"
        />
      </div>
    </div>
    <!-- Legend positioned below chart title -->
    <div class="mb-2 flex flex-wrap items-center justify-center gap-4 text-xs">
      <div class="flex items-center gap-1">
        <div class="w-3 h-3 rounded-full bg-green-500"></div>
        <span>High-Quality</span>
      </div>
      <div class="flex items-center gap-1">
        <div class="w-3 h-3 rounded-full bg-red-500"></div>
        <span>Low-Quality (by this flag)</span>
      </div>
      <div class="flex items-center gap-1">
        <div class="w-3 h-3 rounded-full bg-amber-500"></div>
        <span>Low-Quality (by other flag)</span>
      </div>
      <div class="flex items-center gap-1">
        <div class="w-4 h-0.5 bg-gray-500" style="background-image: repeating-linear-gradient(to right, #6b7280 0px, #6b7280 3px, transparent 3px, transparent 6px);"></div>
        <span>Threshold: {{ formattedThreshold }}</span>
      </div>
    </div>
    <canvas ref="canvasElement" class="w-full h-32"></canvas>
    <CurveSelectionTooltip ref="curveSelectionTooltip" from-source="curve-results" />
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import CurveSelectionTooltip from './CurveSelectionTooltip.vue'
import ChartDownloadButton from './ChartDownloadButton.vue'
import { useLabPath } from '../composables/useLabPath.js'

// Register Chart.js components
Chart.register(...registerables)

export default {
  name: 'FlagThresholdChart',
  components: {
    CurveSelectionTooltip,
    ChartDownloadButton
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  props: {
    allValues: {
      type: Array,
      required: true
    },
    valueIndices: {
      type: Array,
      default: () => []
    },
    threshold: {
      type: Number,
      required: true
    },
    metricLabel: {
      type: String,
      required: true
    },
    flagType: {
      type: String,
      required: true,
      validator: value => ['above', 'below'].includes(value)
    },
    validCurves: {
      type: Array,
      default: () => []
    },
    invalidCurves: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null,
      isChartReady: false,
      chartContainer: null,
      isUnmounting: false
    }
  },
  computed: {
    // Check if this metric should display in minutes (for duration-based flags)
    isDurationInMinutes() {
      return this.metricLabel.includes('Rise Complete') || this.metricLabel.includes('Fall Complete')
    },
    // Check if this metric is a percentage (ends with %)
    isPercentage() {
      return this.metricLabel.endsWith('%')
    },
    // Format threshold for display
    formattedThreshold() {
      if (this.isDurationInMinutes) {
        // Threshold is already in minutes (converted in CurveResults.vue)
        return `${this.threshold.toFixed(1)} minutes`
      }
      if (this.isPercentage) {
        // Convert decimal threshold to percentage
        return `${(this.threshold * 100).toFixed(1)}%`
      }
      return this.threshold.toString()
    },
    chartData() {
      if (!this.allValues || this.allValues.length === 0) {
        console.debug('[FlagThresholdChart] No values provided for', this.metricLabel, {
          allValuesLen: this.allValues ? this.allValues.length : 0,
          validLen: this.validCurves?.length || 0,
          invalidLen: this.invalidCurves?.length || 0
        })
        return {
          datasets: []
        }
      }

      // Group curves by value and category separately
      const categoryValueCounts = {}
      this.allValues.forEach((value, index) => {
        // Map to original curve index when provided to keep alignment with valid/invalid arrays
        const originalIndex = (this.valueIndices && this.valueIndices.length === this.allValues.length)
          ? this.valueIndices[index]
          : index
        
        // Convert value based on metric type:
        // - Duration metrics: convert from hours to minutes
        // - Percentage metrics: convert from decimal to percentage (multiply by 100)
        // - Other metrics: use as-is
        let displayValue
        if (this.isDurationInMinutes) {
          displayValue = Number(value) * 60 // Convert hours to minutes
        } else if (this.isPercentage) {
          displayValue = Number(value) * 100 // Convert decimal to percentage
        } else {
          displayValue = Number(value)
        }
        const valueKey = displayValue.toFixed(6) // Use consistent precision for grouping
        
        if (index === 0) {
          console.debug('[FlagThresholdChart] Index alignment check', {
            metric: this.metricLabel,
            firstValue: value,
            displayValue: displayValue,
            isDurationInMinutes: this.isDurationInMinutes,
            isPercentage: this.isPercentage,
            mappedOriginalIndex: originalIndex,
            allValuesLen: this.allValues.length,
            valueIndicesLen: this.valueIndices?.length || 0,
            validLen: this.validCurves?.length || 0,
            invalidLen: this.invalidCurves?.length || 0,
            threshold: this.threshold,
            flagType: this.flagType
          })
        }
        
        // Evaluate THIS flag independently based on value vs threshold (ignore override statuses)
        // For comparison, convert threshold to same units as displayValue
        let thresholdForComparison = this.threshold
        if (this.isPercentage) {
          thresholdForComparison = this.threshold * 100 // Convert decimal threshold to percentage
        } else if (this.isDurationInMinutes) {
          thresholdForComparison = this.threshold // Already in minutes
        }
        
        let isThisFlagExceeded = false
        if (this.flagType === 'above') {
          isThisFlagExceeded = displayValue > thresholdForComparison
        } else {
          isThisFlagExceeded = displayValue < thresholdForComparison
        }
        
        // Determine category based on flag evaluation, not override status
        // Use override status only as a hint for "invalidByOtherFlag" category
        const isValidCurve = this.validCurves.includes(originalIndex)
        const isInvalidCurve = this.invalidCurves.includes(originalIndex)
        
        let category
        if (isThisFlagExceeded) {
          // THIS flag is exceeded - categorize as "Low-Quality (by this flag)" regardless of override status
          category = 'invalidByThisFlag'
        } else if (isInvalidCurve) {
          // THIS flag is NOT exceeded, but curve is invalid - must be due to other flags
          category = 'invalidByOtherFlag'
        } else {
          // THIS flag is NOT exceeded - categorize as "High-Quality"
          category = 'valid'
        }
        
        // Create unique key for value + category combination (use displayValue for grouping)
        const categoryKey = `${valueKey}-${category}`
        
        if (!categoryValueCounts[categoryKey]) {
          categoryValueCounts[categoryKey] = {
            value: displayValue, // Store display value (in minutes if duration flag)
            category: category,
            count: 0,
            curveIndices: []
          }
        }
        
        categoryValueCounts[categoryKey].count++
        categoryValueCounts[categoryKey].curveIndices.push(originalIndex)
      })

      // Group points by x value (with tolerance for floating point precision)
      // This allows us to distribute points at similar x values evenly
      const xValueGroups = {}
      const xTolerance = 0.01 // Points within 0.01 units are considered at the same x
      
      Object.values(categoryValueCounts).forEach(item => {
        const displayValue = item.value
        // Find existing group for this x value (within tolerance)
        let groupKey = null
        for (const key in xValueGroups) {
          if (Math.abs(Number(key) - displayValue) < xTolerance) {
            groupKey = key
            break
          }
        }
        
        // Create new group if no match found
        if (!groupKey) {
          groupKey = displayValue.toFixed(6)
          xValueGroups[groupKey] = []
        }
        
        // Set colors based on category
        let backgroundColor, borderColor
        switch (item.category) {
          case 'valid':
            backgroundColor = '#10b981' // green
            borderColor = '#059669'
            break
          case 'invalidByThisFlag':
            backgroundColor = '#ef4444' // red
            borderColor = '#dc2626'
            break
          case 'invalidByOtherFlag':
            backgroundColor = '#f59e0b' // amber
            borderColor = '#d97706'
            break
        }
        
        const radius = Math.max(4, Math.min(20, 4 + (item.count * 2))) // Scale from 4 to 20 based on count
        
        xValueGroups[groupKey].push({
          x: displayValue,
          count: item.count,
          curveIndices: item.curveIndices,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          radius: radius,
          category: item.category
        })
      })
      
      // Create data points with proximity-aware jitter
      // Points at the same x value are distributed evenly along y-axis
      const dataPoints = []
      const yRange = 3.6 // Total y-axis range for jitter (-1.8 to +1.8, within -2.0 to +2.0 bounds)
      const minSpacing = 0.2 // Minimum spacing between points to prevent overlap (increased for better separation)
      
      Object.entries(xValueGroups).forEach(([groupKey, points]) => {
        const numPoints = points.length
        
        // Calculate spacing based on number of points and their radii
        // Ensure points don't overlap by considering their radii
        const maxRadius = Math.max(...points.map(p => p.radius))
        const requiredSpacing = Math.max(minSpacing, (maxRadius * 2) / 100) // Convert radius to y-axis units
        const totalRequiredSpace = (numPoints - 1) * requiredSpacing
        
        // If we need more space than available, increase the range for this group
        let effectiveYRange = yRange
        if (totalRequiredSpace > yRange) {
          effectiveYRange = totalRequiredSpace + (requiredSpacing * 2) // Add padding
        }
        
        // Distribute points evenly within the y range
        const startY = -effectiveYRange / 2
        const stepY = numPoints > 1 ? effectiveYRange / (numPoints - 1) : 0
        
        points.forEach((point, index) => {
          // Calculate y position: evenly distributed, centered around 0
          let yPosition
          if (numPoints === 1) {
            // Single point: place at center
            yPosition = 0
          } else {
            // Multiple points: distribute evenly
            yPosition = startY + (index * stepY)
          }
          
          // Add small deterministic offset based on category to help separate different categories
          // This ensures different categories at the same x value are slightly offset
          const categoryOffset = {
            'valid': 0,
            'invalidByThisFlag': 0.02,
            'invalidByOtherFlag': -0.02
          }[point.category] || 0
          
          yPosition += categoryOffset
          
          dataPoints.push({
            x: point.x,
            y: yPosition,
            count: point.count,
            curveIndices: point.curveIndices,
            backgroundColor: point.backgroundColor,
            borderColor: point.borderColor,
            radius: point.radius,
            category: point.category
          })
        })
      })

      console.debug('[FlagThresholdChart] Built data points', {
        metric: this.metricLabel,
        distinctBuckets: Object.keys(categoryValueCounts).length,
        points: dataPoints.length,
        sample: dataPoints.slice(0, 5)
      })

      // Ensure all data points have valid radius values
      const validatedDataPoints = dataPoints.map(point => {
        const radius = Math.max(4, Math.min(20, Number(point.radius) || 4))
        return {
          ...point,
          radius: radius
        }
      })

      return {
        datasets: [{
          label: this.metricLabel,
          data: validatedDataPoints,
          backgroundColor: validatedDataPoints.map(point => point.backgroundColor),
          borderColor: validatedDataPoints.map(point => point.borderColor),
          borderWidth: 1,
          pointRadius: validatedDataPoints.map(point => point.radius),
          pointHoverRadius: validatedDataPoints.map(point => Math.max(point.radius + 2, point.radius))
        }]
      }
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 35, // Increased padding for x-axis label and spacing
            left: 10,
            right: 10
          }
        },
        plugins: {
          title: {
            display: false // Disable Chart.js built-in title
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: false, // Disable default tooltip
            external: (context) => {
              // Use shared tooltip component
              const { chart, tooltip } = context
              const tooltipComponent = this.$refs.curveSelectionTooltip
              
              if (!tooltipComponent || !chart || !chart.canvas) return
              
              // Show tooltip only when hovering over data points
              if (tooltip && tooltip.dataPoints && tooltip.dataPoints.length > 0) {
                const dataPoint = tooltip.dataPoints[0]
                // Safely access the raw point data
                if (dataPoint && dataPoint.raw) {
                  const point = dataPoint.raw
                  // Validate point has required properties
                  if (point && typeof point.x !== 'undefined' && point.curveIndices) {
                    // Create tooltip content using shared component
                    const content = tooltipComponent.createTooltipHtml(
                      point.x, 
                      point.curveIndices, 
                      point.category, 
                      point.count
                    )
                    
                    tooltipComponent.updateTooltipContent(content)
                    
                    // Position tooltip
                    const canvasRect = chart.canvas.getBoundingClientRect()
                    const mouseX = canvasRect.left + (tooltip.caretX || 0)
                    const mouseY = canvasRect.top + (tooltip.caretY || 0)
                    
                    tooltipComponent.positionTooltip(mouseX, mouseY)
                  }
                }
              } else {
                // Hide tooltip when not hovering over data points
                tooltipComponent.hideTooltip()
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            title: {
              display: true,
              // metricLabel already includes units for duration metrics (e.g., "Rise Complete (minutes)")
              // Only append units if not already present
              text: this.metricLabel.includes('(minutes)') || this.metricLabel.includes('(%)')
                ? this.metricLabel
                : this.metricLabel + (this.isDurationInMinutes ? ' (minutes)' : (this.isPercentage ? ' (%)' : '')),
              padding: {
                top: 10,  // Space between axis and label
                bottom: 10 // Space below the label
              }
            },
            ticks: {
              callback: (value) => {
                if (this.isPercentage) {
                  return `${value.toFixed(1)}%`
                }
                if (this.isDurationInMinutes) {
                  // For duration-based flags, only show labels at 30-minute intervals
                  if (Math.abs(value % 30) < 0.1 || Math.abs(value % 30 - 30) < 0.1) {
                    return value.toFixed(1)
                  }
                  return '' // Hide labels that aren't at 30-minute intervals
                }
                // For other numeric values, format to 1 decimal place to avoid floating-point precision issues
                return typeof value === 'number' ? value.toFixed(1) : value
              },
              // For duration-based flags, use stepSize to generate ticks at 30-minute intervals
              ...(this.isDurationInMinutes ? {
                stepSize: 30,
                // Ensure ticks are generated at 30-minute intervals
                maxTicksLimit: 50 // Allow enough ticks for full day range
              } : {})
            },
            grid: {
              display: true
            }
          },
          y: {
            display: false,
            min: -2.0, // Increased range to accommodate proximity-aware jitter
            max: 2.0,
            ticks: {
              display: false
            },
            grid: {
              display: false
            }
          }
        },
        onClick: (event, elements) => {
          if (elements.length > 0 && this.chart && this.chart.data && this.chart.data.datasets) {
            const element = elements[0]
            // Validate element indices before accessing
            if (element.datasetIndex >= 0 && 
                element.index >= 0 && 
                this.chart.data.datasets[element.datasetIndex] &&
                this.chart.data.datasets[element.datasetIndex].data &&
                this.chart.data.datasets[element.datasetIndex].data[element.index]) {
              const dataPoint = this.chart.data.datasets[element.datasetIndex].data[element.index]
              if (dataPoint && dataPoint.curveIndices && dataPoint.curveIndices.length > 0) {
                // Open DataExplorer with the first curve ID
                const curveIndex = dataPoint.curveIndices[0]
                this.openDataExplorerForCurve(curveIndex)
              }
            }
          }
        },
        onHover: (event, activeElements) => {
          // Safety check: ensure event and elements are valid
          if (!event || !activeElements) return
          
          // Handle cursor and tooltip
          if (event.native && event.native.target) {
            try {
              event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default'
            } catch (e) {
              // Ignore errors setting cursor
            }
          }
          // Handle tooltip show/hide using shared component
          const tooltipComponent = this.$refs.curveSelectionTooltip
          if (activeElements.length === 0 && tooltipComponent) {
            tooltipComponent.hideTooltip()
          }
        },
        // Add interaction mode configuration to prevent errors
        interaction: {
          intersect: false,
          mode: 'point'
        }
      }
    }
  },
  async mounted() {
    await this.$nextTick()
    // Set container reference
    this.chartContainer = this.$refs.chartContainer
    this.createChart()
  },
  beforeUnmount() {
    // Set flag to prevent watchers from firing
    this.isUnmounting = true
    
    // Destroy chart
    if (this.chart && !this.chart.destroyed) {
      try {
        this.chart.destroy()
      } catch (error) {
        console.warn('FlagThresholdChart: Error destroying chart during unmount:', error)
      }
    }
    // Reset chart state
    this.chart = null
    this.isChartReady = false
    this.chartContainer = null
  },
  watch: {
    chartData: {
      handler() {
        if (this.isUnmounting) return
        if (this.chart && !this.chart.destroyed) {
          try {
            this.chart.data = this.chartData
            this.chart.update()
            // Ensure ready state is maintained
            this.isChartReady = this.chart && !this.chart.destroyed
          } catch (error) {
            console.warn('FlagThresholdChart: Error updating chart data:', error)
            this.isChartReady = false
          }
        }
      },
      deep: true
    },
    chartOptions: {
      handler() {
        if (this.isUnmounting) return
        if (this.chart && !this.chart.destroyed) {
          try {
            this.chart.options = this.chartOptions
            this.chart.update()
            // Ensure ready state is maintained
            this.isChartReady = this.chart && !this.chart.destroyed
          } catch (error) {
            console.warn('FlagThresholdChart: Error updating chart options:', error)
            this.isChartReady = false
          }
        }
      },
      deep: true
    }
  },
  methods: {
    createChart() {
      if (this.chart) {
        this.chart.destroy()
      }

      if (!this.$refs.canvasElement) {
        console.error('Chart container not found')
        return
      }

      // Validate chart data before creating chart
      if (!this.chartData || !this.chartData.datasets || this.chartData.datasets.length === 0) {
        console.warn('[FlagThresholdChart] No chart data available, skipping chart creation')
        this.isChartReady = false
        return
      }

      // Ensure data points are valid
      const dataset = this.chartData.datasets[0]
      if (!dataset.data || dataset.data.length === 0) {
        console.warn('[FlagThresholdChart] No data points available, skipping chart creation')
        this.isChartReady = false
        return
      }

      // Ensure radius arrays match data length and are properly formatted
      if (dataset.pointRadius && dataset.pointRadius.length !== dataset.data.length) {
        console.warn('[FlagThresholdChart] pointRadius array length mismatch, fixing...')
        dataset.pointRadius = dataset.data.map((point, idx) => {
          const radius = dataset.pointRadius[idx] || (point && point.radius) || 4
          return Math.max(4, Math.min(20, Number(radius) || 4))
        })
      }
      if (dataset.pointHoverRadius && dataset.pointHoverRadius.length !== dataset.data.length) {
        console.warn('[FlagThresholdChart] pointHoverRadius array length mismatch, fixing...')
        dataset.pointHoverRadius = dataset.data.map((point, idx) => {
          const baseRadius = dataset.pointHoverRadius[idx] || (point && point.radius) || 4
          return Math.max(6, Math.min(22, (Number(baseRadius) || 4) + 2))
        })
      }

      // Ensure all data points have valid x and y values
      const originalData = [...dataset.data]
      const validData = []
      const validIndices = []
      
      originalData.forEach((point, idx) => {
        if (point && typeof point.x !== 'undefined' && typeof point.y !== 'undefined' && !isNaN(point.x) && !isNaN(point.y)) {
          validData.push(point)
          validIndices.push(idx)
        }
      })
      
      if (validData.length !== originalData.length) {
        console.warn(`[FlagThresholdChart] Filtered ${originalData.length - validData.length} invalid data points`)
        dataset.data = validData
        // Rebuild radius arrays to match filtered data
        if (dataset.pointRadius) {
          dataset.pointRadius = validIndices.map(origIdx => {
            const radius = dataset.pointRadius[origIdx] || (originalData[origIdx] && originalData[origIdx].radius) || 4
            return Math.max(4, Math.min(20, Number(radius) || 4))
          })
        }
        if (dataset.pointHoverRadius) {
          dataset.pointHoverRadius = validIndices.map(origIdx => {
            const baseRadius = dataset.pointHoverRadius[origIdx] || (originalData[origIdx] && originalData[origIdx].radius) || 4
            return Math.max(6, Math.min(22, (Number(baseRadius) || 4) + 2))
          })
        }
      }

      // Final validation: ensure we still have data after filtering
      if (dataset.data.length === 0) {
        console.warn('[FlagThresholdChart] No valid data points after filtering, skipping chart creation')
        this.isChartReady = false
        return
      }

      const ctx = this.$refs.canvasElement.getContext('2d')
      
      try {
        this.chart = new Chart(ctx, {
          type: 'scatter',
          data: this.chartData,
          options: {
            ...this.chartOptions,
            plugins: {
              ...this.chartOptions.plugins,
              thresholdLine: {
                threshold: this.threshold,
                flagType: this.flagType
              }
            },
            // Ensure elements are properly configured to prevent hitRadius errors
            elements: {
              point: {
                radius: 4,
                hoverRadius: 6,
                hitRadius: 10
              }
            }
          },
          plugins: [this.createThresholdLinePlugin()]
        })

        // Mark chart as ready after creation
        this.isChartReady = true
      } catch (error) {
        console.error('[FlagThresholdChart] Error creating chart:', error)
        this.isChartReady = false
      }
    },
    createThresholdLinePlugin() {
      return {
        id: 'thresholdLine',
        afterDraw: (chart) => {
          const { ctx, chartArea, scales } = chart
          
          if (!chartArea) return

          const xScale = scales.x
          // Convert threshold to display units for positioning
          let thresholdForDisplay = this.threshold
          if (this.isPercentage) {
            thresholdForDisplay = this.threshold * 100 // Convert decimal to percentage
          } else if (this.isDurationInMinutes) {
            thresholdForDisplay = this.threshold // Already in minutes
          }
          const thresholdX = xScale.getPixelForValue(thresholdForDisplay)
          
          // Only draw if threshold is within chart bounds
          if (thresholdX >= chartArea.left && thresholdX <= chartArea.right) {
            ctx.save()
            ctx.strokeStyle = '#6b7280' // gray color
            ctx.lineWidth = 2
            ctx.setLineDash([5, 5]) // dashed line
            ctx.beginPath()
            ctx.moveTo(thresholdX, chartArea.top)
            ctx.lineTo(thresholdX, chartArea.bottom)
            ctx.stroke()
            ctx.restore()

          }
        }
      }
    },
    openDataExplorerForCurve(curveIndex) {
      // Navigate to Explorer with the from parameter to indicate we came from Curve Results
      try {
        console.log(`✅ Navigating to Explorer in same tab for curve ${curveIndex}`)
        this.$router.push(this.toLab(`/explorer?curve=${curveIndex}&from=curve-results`))
      } catch (error) {
        console.error('❌ Error navigating to DataExplorer:', error)
      }
    }
  }
}
</script>

<style scoped>
.flag-threshold-chart {
  width: 100%;
  height: 220px; /* Increased to accommodate larger jitter range and prevent clipping */
}
</style>
